import { Button, FileInput, Group, NumberInput, SimpleGrid, Stack, Table, Text, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useAddLab, useDeleteLab, useImportLab, useLab } from "../../api/generated/stockvol";
import type { LabImported, PageLabOut } from "../../api/generated/model";
import { useSiteInfo } from "../../api/hooks";
import { Section, TableCard } from "../../components/Section";
import { act, confirm } from "../../lib/actions";
import { fmtLab, labAttrs, type LabKey } from "../../lib/lab";
import { formatLocal, localToUtcIso, toLocalInput } from "../../lib/time";

const data = <T,>(r: unknown) => (r as { data: T }).data;
const REFRESH = ["/api/lab", "blocks", "reclaimed"];
const H2 = 2 * 3_600_000;

/** The lab's results per cycle (M7.9): one by hand, a CSV, the list with Delete. */
export function LabResults() {
  const site = useSiteInfo();
  const tz = site.data?.timezone ?? "UTC";
  const attrs = labAttrs(site.data);
  const qc = useQueryClient();
  const lab = useLab({ limit: 100 }, { query: { select: data<PageLabOut> } });
  const add = useAddLab();
  const imp = useImportLab();
  const del = useDeleteLab();
  // the last whole 2-hour cycle
  const cycleEnd = Math.floor(Date.now() / H2) * H2;
  const [start, setStart] = useState(() => toLocalInput(cycleEnd - H2, tz));
  const [end, setEnd] = useState(() => toLocalInput(cycleEnd, tz));
  const [product, setProduct] = useState("");
  const [tonnes, setTonnes] = useState<number | string>("");
  const [values, setValues] = useState<Record<string, number | string>>({});
  const [file, setFile] = useState<File | null>(null);
  const num = (v: number | string | undefined) => (v === "" || v === undefined ? null : Number(v));
  const anyValue = attrs.some((a) => num(values[a.key]) !== null);
  const submit = () =>
    void act(
      () =>
        add.mutateAsync({
          data: {
            t_start: localToUtcIso(start, tz),
            t_end: localToUtcIso(end, tz),
            product: product || null,
            tonnes_t: num(tonnes),
            ...Object.fromEntries(attrs.map((a) => [a.key, num(values[a.key])])),
          },
        }),
      { done: "Lab result added", qc, refresh: REFRESH },
    );
  return (
    <Stack gap="lg">
      <Section title="Add a result or import a CSV">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 4 }}>
            <TextInput type="datetime-local" label={`From (${tz})`} value={start} onChange={(e) => setStart(e.currentTarget.value)} />
            <TextInput type="datetime-local" label="To" value={end} onChange={(e) => setEnd(e.currentTarget.value)} />
            <TextInput label="Product (optional)" value={product} onChange={(e) => setProduct(e.currentTarget.value)} />
            <NumberInput label="Tonnes (optional)" value={tonnes} onChange={setTonnes} min={0} />
            {attrs.map((a) => (
              <NumberInput
                key={a.key}
                label={a.unit ? `${a.label}, ${a.unit}` : a.label}
                value={values[a.key] ?? ""}
                onChange={(v) => setValues((s) => ({ ...s, [a.key]: v }))}
                min={0}
                decimalScale={a.decimals}
              />
            ))}
            <Group align="flex-end">
              <Button onClick={submit} loading={add.isPending} disabled={!anyValue}>
                Add result
              </Button>
            </Group>
          </SimpleGrid>
          <Text size="xs" c="dimmed">
            A result for a cycle that already has one replaces it. Leave a value empty when the lab did not report it.
          </Text>
          <Group align="flex-end">
            <FileInput
              label="Import a CSV (t_start,t_end,ash_pct,tm_pct,im_pct,vm_pct,g_index; ISO times)"
              accept=".csv,text/csv"
              value={file}
              onChange={setFile}
              w={460}
            />
            <Button
              variant="light"
              disabled={!file}
              loading={imp.isPending}
              onClick={() =>
                file &&
                confirm(
                  "Import lab results",
                  `Import every row of ${file.name}? A row for a cycle that already has a result replaces it. A bad row stops the whole import.`,
                  () =>
                    void act(
                      async () => {
                        const r = data<LabImported>(await imp.mutateAsync({ data: { file } }));
                        return r;
                      },
                      { done: `${file.name} imported`, qc, refresh: REFRESH },
                    ),
                )
              }
            >
              Import
            </Button>
          </Group>
        </Stack>
      </Section>
      <TableCard title="Lab results">
        <Table highlightOnHover fz="sm" data-testid="lab-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>From</Table.Th>
              <Table.Th>To</Table.Th>
              <Table.Th>Product</Table.Th>
              {attrs.map((a) => (
                <Table.Th key={a.key} ta="right">
                  {a.unit ? `${a.label} ${a.unit}` : a.label}
                </Table.Th>
              ))}
              <Table.Th>Source</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lab.data?.items.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>{formatLocal(r.t_start, tz)}</Table.Td>
                <Table.Td>{formatLocal(r.t_end, tz, "time")}</Table.Td>
                <Table.Td>{r.product ?? <Text span c="dimmed">—</Text>}</Table.Td>
                {attrs.map((a) => (
                  <Table.Td key={a.key} ta="right" className="sv-num">
                    {fmtLab(r[a.key as LabKey], a)}
                  </Table.Td>
                ))}
                <Table.Td>{r.source}</Table.Td>
                <Table.Td>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    onClick={() =>
                      confirm("Delete result", "Remove this lab result?", () =>
                        void act(() => del.mutateAsync({ labId: r.id }), { done: "Lab result removed", qc, refresh: REFRESH }),
                      )
                    }
                  >
                    Delete
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {lab.data && lab.data.items.length === 0 && (
          <Text size="sm" c="dimmed" p="lg">
            No lab results yet. Add one above, or import the lab's sheet as a CSV.
          </Text>
        )}
      </TableCard>
    </Stack>
  );
}
